import { useContext, useEffect } from 'react'
import { createContext } from 'react'

import { useCableSubscription } from '../CableSubscriptionProvider'
import useChatThreads from '../ChatThreads/hooks/useChatThreads'
import useSetChatThreadEvent from './utils/useSetChatThreadEvent'

type ContextState = ReturnType<typeof useChatThreads>

const StoreContext = createContext<ContextState | undefined>(undefined)

type SessionChatThreadsProviderProps = React.PropsWithChildren

const BASE_PAGINATION = {
  offset: 0,
  size: 1,
}

const BASE_SORT = {
  field: 'id',
  order: 'descend',
} as const

function SessionChatThreadsProvider({ children }: SessionChatThreadsProviderProps) {
  const state = useChatThreads(BASE_PAGINATION, BASE_SORT)
  const setChatThreadEvent = useSetChatThreadEvent(state.reload)
  const { subscription, loaded } = useCableSubscription()

  useEffect(() => {
    if (loaded) {
      subscription.on('message', data => {
        if (
          data.action === 'chat_thread_event_created' ||
          data.action === 'chat_thread_event_updated' ||
          data.action === 'new_chat_thread_event'
        ) {
          setChatThreadEvent(data.resource.chat_thread_event.data)
        }
      })
    }
  }, [loaded])

  return <StoreContext.Provider value={state}>{children}</StoreContext.Provider>
}

export const useSessionChatThreads = () => {
  const context = useContext(StoreContext)
  if (!context) {
    throw new Error('useSessionChatThreads must be used within a SessionChatThreadsProvider')
  }
  return context
}

export default SessionChatThreadsProvider
